// FormControl.tsx
import React, { useState } from 'react';
import ControlRenderer, { Control } from '../ControlRenderer';
import { get, isEmpty, set } from '../utils/Utils';
import { FormModeType } from '@/components/FormDataProvider';
import EditIcon from '@mui/icons-material/Edit';
import AddControlIcon from '@mui/icons-material/AddCircleOutline';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutline';
import MenuIcon from '@mui/icons-material/ListAlt';
import { IconButton, Menu, MenuItem, Typography } from '@mui/material';
import ControlEditor, { getDefaultControl } from '../ControlEditor';

/**
* Defines a grid control that integrates rows and columns, extending the Control interface.
*/
export interface FormControl extends Control {
  /**
   * The form version.
   */
  version: number;

  /**
   * The form can be reused within controls.
   */
  isReusable: boolean;
}

interface FormControlProps {
  control: Control;
  data: any;
  onDataChange: (newData: any) => void;
  mode: FormModeType;
  onParentMenuClick?: (event: React.MouseEvent<HTMLElement>) => void; // called when a parent menu is clicked
}

const FormControl: React.FC<FormControlProps> = ({ control, data, onDataChange, mode, onParentMenuClick }) => {

  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuAnchorElAdd, setMenuAnchorElAdd] = React.useState<null | HTMLElement>(null);
  const [currentControl, setCurrentControl] = React.useState<Control | null>(null);
  const [editControl, setEditControl] = React.useState<Control | null>(null);

  const handleSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorElAdd(event.currentTarget);
  };

  const handleSubmenuClose = () => {
    setMenuAnchorElAdd(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, control: Control | null) => {
    event.stopPropagation();
    setCurrentControl(control);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuOpenEmpty = (event: React.MouseEvent<HTMLElement>, row: number, column: number) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setCurrentControl(null);
  };

  var formContext: any;
  if (control.dataKey) {
    formContext = get(data, control.dataKey, undefined);
    if (!formContext) {
      formContext = control.dataContextKey === 'FORMDATA' ? [] : {};
      set(data, control.dataKey, formContext)
      console.log(`creating a missing datacontext ${control.dataKey}`);
    }
  }
  else {
    formContext = data;
  }

  const formControl = control as FormControl;

  const handleEditControl = (controlToEdit: Control) => {
    setEditControl(controlToEdit);
  };

  const handleAddControl = (type: string) => {
    var newControl = getDefaultControl(type);
    if (currentControl) {
      // adding to control
      currentControl.controls?.push(newControl);
    } else {
      control.controls?.push(newControl);
    }

    setEditControl(newControl);
    setMenuAnchorEl(null);
    setMenuAnchorElAdd(null);
  };

  const handleRemoveControl = (controlToRemove: Control) => {
    // Remove the control from the grid's controls
    const updatedControls = formControl.controls?.filter((c: Control) => c !== controlToRemove);

    // Update the grid control's controls
    // Assuming you have a setControls function or similar to update state
    set(control, 'controls', updatedControls);

    // Trigger parent update if necessary
    onDataChange(control.name);
  };

  const firstControl = control.controls && control.controls.length > 0 ? control.controls[0] : null;

  return (
    <div style={{ position: 'relative' }}>
      {firstControl &&
        <ControlRenderer
          control={firstControl}
          data={formContext}
          onDataChange={(newData: any) =>
            onDataChange(newData)
          }
          mode={mode}
          onParentMenuClick={(event) => handleMenuOpen(event, firstControl)}
        />
      }

      {mode === 'design' && (
        <>
          {(!firstControl || (firstControl.type !== 'grid' && firstControl.type !== 'datagrid' && firstControl.type !== 'flex')) && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              display: 'flex'
            }}>
              <IconButton
                style={{
                  padding: 0,
                  color: 'gray',
                  zIndex: 1000
                }}
                onClick={(event) => handleMenuOpen(event, firstControl)}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            </div>
          )}
        </>
      )}
      {menuAnchorEl && (<Menu
        id="control-menu"
        disableScrollLock
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {onParentMenuClick &&
          <MenuItem onClick={(event) => { onParentMenuClick(event); }}>
            <MenuIcon className="eform-menu-icon" />
            <Typography variant="inherit">Parent Control</Typography>
          </MenuItem>
        }
        {!onParentMenuClick &&
          <MenuItem onClick={() => { handleEditControl(control); handleMenuClose(); }}>
            <EditIcon className="eform-menu-icon" style={{ color: "green" }} />
            <Typography variant="inherit">{`Edit ${control.type} Properties`}</Typography>
          </MenuItem>
        }
        {currentControl && <>
          <MenuItem onClick={() => { handleEditControl(currentControl); handleMenuClose(); }}>
            <EditIcon className="eform-menu-icon" style={{ color: "green" }} />
            <Typography variant="inherit">{`Edit ${currentControl.type} Properties`}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleRemoveControl(currentControl); handleMenuClose(); }}>
            <RemoveIcon className="eform-menu-icon" style={{ color: "red" }} />
            <Typography variant="inherit">{`Remove Control (${currentControl.type})`}</Typography>
          </MenuItem>
        </>}
        {(!currentControl || currentControl.type === "tab" || currentControl.type === "flex") && <>
          <MenuItem onClick={handleSubmenuOpen}>
            <AddControlIcon className="eform-menu-icon" style={{ color: "green" }} />
            <Typography variant="inherit">{currentControl ? `Add Item to ${currentControl.type} Control` : "Add Control"}</Typography>
          </MenuItem>
          <Menu
            id="addmenu"
            anchorEl={menuAnchorElAdd}
            open={Boolean(menuAnchorElAdd)}
            onClose={handleSubmenuClose}
          >
            <MenuItem onClick={() => { handleAddControl("label") }}>
              <Typography variant="inherit">Label</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("text") }}>
              <Typography variant="inherit">Text</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("textarea") }}>
              <Typography variant="inherit">TextArea</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("select") }}>
              <Typography variant="inherit">Dropdown</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("date") }}>
              <Typography variant="inherit">DatePicker</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("check") }}>
              <Typography variant="inherit">Checkbox</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("button") }}>
              <Typography variant="inherit">Button</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("grid") }}>
              <Typography variant="inherit">Grid</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("flex") }}>
              <Typography variant="inherit">FlexGrid</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("datagrid") }}>
              <Typography variant="inherit">DataGrid</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("tab") }}>
              <Typography variant="inherit">Tab Control</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleAddControl("upload") }}>
              <Typography variant="inherit">Upload Control</Typography>
            </MenuItem>
          </Menu>
        </>}
      </Menu>)
      }
      {editControl &&
        <ControlEditor
          controlToEdit={editControl}
          data={formContext}
          dataKey={control.dataKey}
          onComplete={() => { setEditControl(null) }}
          onError={() => setEditControl(null)} />}
    </div>
  );
};

export default FormControl;
